import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, LoaderIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
import { submitLead } from '../utils/leadWebhook';
type Status = 'idle' | 'loading' | 'success' | 'error';
type Fields = {
  firstName: string;
  email: string;
  countryCode: string;
  whatsapp: string;
};
type GuideModalProps = {
  open: boolean;
  onClose: () => void;
};
const countryCodes = [{
  code: '+49',
  label: '🇩🇪 +49'
}, {
  code: '+43',
  label: '🇦🇹 +43'
}, {
  code: '+41',
  label: '🇨🇭 +41'
}];
export function GuideModal({
  open,
  onClose
}: GuideModalProps) {
  const [fields, setFields] = useState<Fields>({
    firstName: '',
    email: '',
    countryCode: '+49',
    whatsapp: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Hintergrund sperren, solange das Overlay offen ist
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFields(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
    if (status === 'error') setStatus('idle');
  };
  const [submitError, setSubmitError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    const nextErrors: Partial<Record<keyof Fields, string>> = {};
    if (!fields.firstName.trim()) nextErrors.firstName = 'Bitte gib deinen Vor- und Nachnamen an.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) nextErrors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (!/^[0-9\s/-]{7,20}$/.test(fields.whatsapp.trim())) nextErrors.whatsapp = 'Bitte gib deine Handynummer an.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await submitLead({
        firstName: fields.firstName.trim(),
        email: fields.email.trim(),
        countryCode: fields.countryCode,
        whatsapp: fields.whatsapp.trim()
      });
      setStatus('success');
    } catch {
      setSubmitError('Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.');
      setStatus('error');
    }
  };
  const inputClass = (key: keyof Fields) => `w-full rounded-full border bg-neutral-50 px-5 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-gold focus:bg-cream ${errors[key] ? 'border-red-400' : 'border-neutral-200'}`;

  // Über ein Portal direkt am <body> – so liegt das Overlay immer über der
  // ganzen Seite, unabhängig davon, in welchem Abschnitt es eingebunden ist.
  return createPortal(<AnimatePresence data-id="element-253" data-mp="1i2nmbfitcr3z">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/50 p-4 backdrop-blur-sm" onClick={onClose} data-id="element-254" data-mp="1i1chrdhjnfbt">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="guide-modal-title" initial={{
        opacity: 0,
        scale: 0.94,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md overflow-hidden rounded-xl bg-cream p-6 shadow-2xl sm:p-8" data-id="element-255" data-mp="1n2tklejl9bv1">
            <span aria-hidden="true" className="gold-glitter-layer-static" data-id="element-256" data-mp="41ho46oakbqr" />
            <span aria-hidden="true" className="gold-glitter-extra" data-id="element-257" data-mp="91nl82dbyqg8" />

            <button type="button" onPointerUp={e => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }} onClick={e => {
          e.stopPropagation();
          onClose();
        }} aria-label="Schließen" className="absolute right-2 top-2 z-20 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" data-id="element-258" data-mp="37l42z4x9exvo">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-259" data-mp="1axjm4lcpxm85" />
            </button>

            <div className="relative z-10 text-center" data-id="element-260" data-mp="1avfj05mn8r8x">
              <p className="text-sm font-semibold text-gold" data-id="element-261" data-mp="16wzozhs5aim0">Nur noch 1 Schritt fehlt...</p>
              <h2 id="guide-modal-title" className="font-heading mt-2 text-2xl font-bold text-neutral-900 sm:text-3xl" data-id="element-262" data-mp="11w27z8ivfum3">
                Wohin dürfen wir dir deinen Fahrplan schicken?
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-600" data-id="element-263" data-mp="s0s7wqc19xay">
                Du bekommst den 15-seitigen Guide sofort per E-Mail – kostenlos und unverbindlich.
              </p>
            </div>

            {status === 'success' ? <div role="status" className="relative z-10 mt-6 flex items-start gap-3 rounded-2xl bg-hot-50 p-5" data-id="element-264" data-mp="e82alpr215yj">
                <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-hot-300" aria-hidden="true" data-id="element-265" data-mp="96v0vl5cpwp8" />
                <p className="text-sm text-neutral-800" data-id="element-266" data-mp="4aef2xlmjufb">
                  <strong className="block text-base font-semibold" data-id="element-267" data-mp="3qr4wk9zmxh8m">
                    Yes {fields.firstName.trim()}, geschafft!
                  </strong>
                  Schau auch im Spam-Ordner nach.
                </p>
              </div> : <form onSubmit={handleSubmit} noValidate className="relative z-20 mt-6 space-y-3" data-id="element-268" data-mp="3hs9sb3g5ih7y">
                <div data-id="element-269" data-mp="3ltuzc86081a8">
                  <input type="text" autoComplete="given-name" value={fields.firstName} onChange={update('firstName')} placeholder="Vor- und Nachname" aria-label="Vor- und Nachname" aria-invalid={Boolean(errors.firstName)} className={inputClass('firstName')} data-id="element-270" data-mp="dbytzcm9b2q1" />
                </div>

                <div data-id="element-271" data-mp="w99wd636qc9p">
                  <input type="email" autoComplete="email" value={fields.email} onChange={update('email')} placeholder="Deine geschäftliche E-Mail-Adresse" aria-label="Deine geschäftliche E-Mail-Adresse" aria-invalid={Boolean(errors.email)} className={inputClass('email')} data-id="element-272" data-mp="w901p5773gxo" />
                </div>

                <div className="flex gap-0" data-id="element-273" data-mp="2qj6vf8byqpkh">
                  <div className="relative w-28 shrink-0" data-id="element-274" data-mp="22sudd4n07cg6">
                    <button type="button" onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex h-full w-full items-center justify-between rounded-l-full border border-r-0 border-neutral-200 bg-neutral-50 py-3 pl-4 pr-2 text-sm text-neutral-900 outline-none focus:border-gold focus:bg-cream" data-id="element-275" data-mp="22tciyrvk330r">
                      <span data-id="element-276" data-mp="3o8jphykt5lt3">{countryCodes.find(c => c.code === fields.countryCode)?.label}</span>
                      <svg className="h-4 w-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-277" data-mp="3j9giw9p6j7j0">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-278" data-mp="1ljrtkdq3d4og" />
                      </svg>
                    </button>
                    
                    {countryDropdownOpen && <>
                        <div className="fixed inset-0 z-10" onClick={() => setCountryDropdownOpen(false)} data-id="element-279" data-mp="1vb5y4zwwguog" />
                        <ul className="absolute left-0 top-full z-20 mt-1 max-h-48 w-full overflow-auto rounded-2xl border border-neutral-200 bg-cream py-1 shadow-lg" data-id="element-280" data-mp="248yfsaq3gef0">
                          {countryCodes.map(c => <li key={c.code} data-id="element-281" data-mp="2e7nyg5pte2wb">
                              <button type="button" onClick={() => {
                      setFields(prev => ({
                        ...prev,
                        countryCode: c.code
                      }));
                      setCountryDropdownOpen(false);
                    }} className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-neutral-100" data-id="element-282" data-mp="evuyc07qchfd">
                                {c.label}
                              </button>
                            </li>)}
                        </ul>
                      </>}
                  </div>
                  <input type="tel" inputMode="tel" autoComplete="tel-local" value={fields.whatsapp} onChange={update('whatsapp')} placeholder="Deine Handynummer" aria-label="Deine Handynummer" aria-invalid={Boolean(errors.whatsapp)} className={`w-full rounded-r-full border bg-neutral-50 px-4 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-gold focus:bg-cream ${errors.whatsapp ? 'border-red-400' : 'border-neutral-200'}`} data-id="element-283" data-mp="qaeulh18f6re" />
                </div>

                <button type="submit" disabled={status === 'loading'} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-hot-300 px-6 py-4 text-sm font-semibold text-white shadow-md transition-colors hover:bg-hot-400 disabled:opacity-70" data-id="element-284" data-mp="203wlxhb4w1kz">
                  {status === 'loading' && <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-285" data-mp="2izv4p7ib8nfc" />}
                  Jetzt kostenlos herunterladen
                </button>

                {status === 'error' && <ul role="alert" className="space-y-1 pt-1" data-id="element-286" data-mp="onf2qiwjrvga">
                    {submitError && <li className="flex items-center gap-2 text-xs font-medium text-red-500" data-id="element-287" data-mp="jpwp4f7l4s1y">
                        <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-288" data-mp="2vc78tc7by1m6" />
                        {submitError}
                      </li>}
                    {(Object.keys(errors) as (keyof Fields)[]).map(key => errors[key] ? <li key={key} className="flex items-center gap-2 text-xs font-medium text-red-500" data-id="element-289" data-mp="2qiw0t7qs7eso">
                          <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-290" data-mp="2hksei97z2dzt" />
                          {errors[key]}
                        </li> : null)}
                  </ul>}
              </form>}

            <div className="relative z-10 mt-6 flex flex-col items-center gap-1.5 text-xs text-neutral-600" data-id="element-291" data-mp="wg7ucxu37h7">
              <div className="flex items-center gap-1.5" data-id="element-292" data-mp="5t6op2hgq73o">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-293" data-mp="4d1960pga3ac" />
                <span data-id="element-294" data-mp="4b7ymabpuyx2">Von Frauen, für Frauen.</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-295" data-mp="3viu9oo0rm3ny">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-296" data-mp="3qk97ij1yd9wv" />
                <span data-id="element-297" data-mp="3qmln4pk8n86r">100% Wissen mit Praxis-Garantie!</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-298" data-mp="3mkia3whx7vjd">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-299" data-mp="n4bve4k8drvw" />
                <span data-id="element-300" data-mp="n5wtu59o1uz1">Völlig unverbindlich und kostenfrei.</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-301" data-mp="i5he571wwbdw">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-302" data-mp="dd7idae48h3p" />
                <span data-id="element-303" data-mp="9cdtadoxsw98">Keine unerwünschte Werbung!</span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}